import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/services/analytics";
import { useGlide } from "./glide";
import "./GuidedSpotlight.scss";

export const GUIDE_STORAGE_KEY = "guidedSpotlight";
export const GUIDE_CLOSED_EVENT = "guidedSpotlight:closed";

export const guideIsShowing = () => {
  try {
    return Boolean(sessionStorage.getItem(GUIDE_STORAGE_KEY));
  } catch {
    return false;
  }
};
const WAIT_MS = 10_000;
const MISSES_BEFORE_CLOSE = 4;
const PADDING = 8;
const GAP = 14;
const CARD_W = 340;

const readGuide = () => {
  try {
    return JSON.parse(sessionStorage.getItem(GUIDE_STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

const clearGuide = () => {
  try {
    sessionStorage.removeItem(GUIDE_STORAGE_KEY);
  } catch {}
};

const stepsOf = (guide) => (guide?.steps?.length ? guide.steps : guide ? [guide] : []);

export const queueGuide = (guide) => {
  try {
    sessionStorage.setItem(GUIDE_STORAGE_KEY, JSON.stringify(guide));
  } catch {}
  window.dispatchEvent(new Event(GUIDE_STORAGE_KEY));
};

const isShown = (el) => {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

const findTarget = (selector) => {
  try {
    return [...document.querySelectorAll(selector)].find(isShown) || null;
  } catch {
    return null;
  }
};

export const cardPlacement = (rect, viewport, prefer) => {
  const width = Math.min(CARD_W, viewport.width - 32);
  const beside = rect.left + rect.width + PADDING + GAP;
  if (prefer === "side" && beside + width <= viewport.width - 16) {
    return {
      left: beside,
      width,
      top: Math.min(Math.max(16, rect.top), Math.max(16, viewport.height - 280)),
    };
  }
  const left = Math.min(
    Math.max(16, rect.left + rect.width / 2 - width / 2),
    viewport.width - width - 16,
  );
  const below = rect.bottom + PADDING + GAP;
  const fitsBelow = viewport.height - below > 190;
  return fitsBelow
    ? { left, width, top: below }
    : { left, width, bottom: viewport.height - rect.top + PADDING + GAP };
};

const GuidedSpotlight = () => {
  const location = useLocation();
  const [guide, setGuide] = useState(null);
  const [target, setTarget] = useState(null);
  const [covered, setCovered] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const { rect, moving } = useGlide(guide && !covered ? target : null, location.pathname);

  const close = useCallback(() => {
    const ending = readGuide();
    if (ending?.steps?.length > 1) {
      const at = ending.index || 0;
      trackEvent({
        name: "guided_steps_closed",
        attributes: { label: ending.label || null, step: at, of: ending.steps.length, finished: at === ending.steps.length - 1 },
      });
    }
    clearGuide();
    setTarget(null);
    setGuide(null);
    setBlocked(null);
    setCovered(false);
    window.dispatchEvent(new Event(GUIDE_CLOSED_EVENT));
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const start = () => {
      const queued = readGuide();
      if (!queued || queued.path !== location.pathname) return;
      const current = stepsOf(queued)[queued.index || 0];
      if (!current) {
        clearGuide();
        window.dispatchEvent(new Event(GUIDE_CLOSED_EVENT));
        return;
      }
      const startedAt = Date.now();
      const poll = () => {
        if (cancelled) return;
        const el = findTarget(current.target);
        if (el) {
          setTarget(el);
          if (queued.steps?.length > 1) {
            trackEvent({
              name: "guided_step_viewed",
              attributes: { label: queued.label || null, step: queued.index || 0, of: queued.steps.length },
            });
          }
          setGuide(queued);
          return;
        }
        if (Date.now() - startedAt > WAIT_MS) {
          clearGuide();
          window.dispatchEvent(new Event(GUIDE_CLOSED_EVENT));
          return;
        }
        timer = setTimeout(poll, 250);
      };
      poll();
    };
    start();
    window.addEventListener(GUIDE_STORAGE_KEY, start);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener(GUIDE_STORAGE_KEY, start);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!guide) return undefined;
    const sequence = stepsOf(guide).length > 1;
    const current = stepsOf(guide)[guide.index || 0];
    let misses = 0;
    const check = () => {
      const modalOpen = Boolean(document.querySelector(".modal.show"));
      if (modalOpen && !sequence) return close();
      setCovered(modalOpen);
      if (!target || !target.isConnected) {
        const again = findTarget(current.target);
        if (!again) {
          misses += 1;
          if (misses >= (sequence ? MISSES_BEFORE_CLOSE : 1)) close();
          return;
        }
        setTarget(again);
      }
      misses = 0;
    };
    const timer = setInterval(check, 250);
    const onKey = (e) => e.key === "Escape" && !document.querySelector(".modal.show") && close();
    const onTargetClick = () => close();
    const el = sequence ? null : target;
    el?.addEventListener("click", onTargetClick);
    window.addEventListener("keydown", onKey);
    return () => {
      clearInterval(timer);
      el?.removeEventListener("click", onTargetClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [guide, target, close]);

  useEffect(() => {
    if (guide && guide.path !== location.pathname) close();
  }, [location.pathname, guide, close]);

  const steps = stepsOf(guide);
  const index = guide?.index || 0;
  const sequence = steps.length > 1;

  const goTo = (nextIndex) => {
    const nextStep = steps[nextIndex];
    if (!nextStep) return close();
    const el = findTarget(nextStep.target);
    if (!el) {
      setBlocked(nextIndex);
      return;
    }
    setBlocked(null);
    const nextGuide = { ...guide, index: nextIndex };
    trackEvent({
      name: "guided_step_viewed",
      attributes: { label: guide.label || null, step: nextIndex, of: steps.length },
    });
    try {
      sessionStorage.setItem(GUIDE_STORAGE_KEY, JSON.stringify(nextGuide));
    } catch {}
    setTarget(el);
    setGuide(nextGuide);
  };

  if (!guide || !rect) return null;

  const step = steps[index];
  const isLast = index === steps.length - 1;
  const place = cardPlacement(
    { ...rect, bottom: rect.top + rect.height },
    {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    sequence ? "side" : undefined,
  );

  return createPortal(
    <div className="guided-spotlight" role="dialog" aria-modal="false" aria-labelledby="guided-spotlight-title">
      <div
        className="guided-spotlight__ring"
        style={{
          top: rect.top - PADDING,
          left: rect.left - PADDING,
          width: rect.width + PADDING * 2,
          height: rect.height + PADDING * 2,
        }}
      />
      <div className={`guided-spotlight__card${moving ? " is-moving" : ""}`} style={place}>
        {sequence ? (
          <p className="guided-spotlight__count">
            {guide.label ? `${guide.label} · ` : ""}Step {index + 1} of {steps.length}
          </p>
        ) : null}
        <h4 id="guided-spotlight-title">{step.title}</h4>
        <p>{step.body}</p>
        {blocked !== null && steps[blocked]?.waitFor ? <p className="guided-spotlight__wait">{steps[blocked].waitFor}</p> : null}
        {sequence ? (
          <div className="guided-spotlight__foot guided-spotlight__foot--steps">
            <button type="button" className="guided-spotlight__later" onClick={close}>
              Finish on my own
            </button>
            {index > 0 ? (
              <button type="button" className="guided-spotlight__back" onClick={() => goTo(index - 1)}>
                Back
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => (isLast ? close() : goTo(index + 1))}
            >
              {isLast ? "Got it" : "Next"}
            </button>
          </div>
        ) : (
          <div className="guided-spotlight__foot">
            <button type="button" className="btn btn-primary" onClick={close}>
              Got it
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default GuidedSpotlight;
